// Maps the original app's storage keys to database tables.
//
//   mpc_people_v1                 -> patients
//   mpc_medicines_v1::<patientId> -> medicines
//   mpc_library_v1::<patientId>   -> library_items
//   mpc_reminders_v1::<patientId> -> reminders
//   mpc_visits_v1::<patientId>    -> visits
//   mpc_doctors_v1::<patientId>   -> doctors
//   mpc_tests_v1::<patientId>     -> tests
//   mpc_vaccines_v1::<patientId>  -> vaccinations
//   mpc_types_v1                  -> medicine_types
//   mpc_testlib_v1                -> test_library
//   mpc_active_person_v1, medcalc_calc_range_v1        -> user_settings (per account)
//   mpc_notif_perm_v1, mpc_bell_alerted_v1             -> this device only
//   anything else (e.g. mpc_default_icon_v1)           -> family_settings
import type { DataTable, PatientTable } from './types';

export const PEOPLE_KEY = 'mpc_people_v1';

const PATIENT_PREFIX: Record<string, PatientTable> = {
  mpc_medicines_v1: 'medicines',
  mpc_library_v1: 'library_items',
  mpc_reminders_v1: 'reminders',
  mpc_visits_v1: 'visits',
  mpc_doctors_v1: 'doctors',
  mpc_tests_v1: 'tests',
  mpc_vaccines_v1: 'vaccinations',
};
const TABLE_PREFIX: Record<PatientTable, string> = Object.fromEntries(
  Object.entries(PATIENT_PREFIX).map(([k, v]) => [v, k]),
) as Record<PatientTable, string>;

const FAMILY_LIST_KEYS: Record<string, DataTable> = {
  mpc_types_v1: 'medicine_types',
  mpc_testlib_v1: 'test_library',
};
const LIST_TABLE_KEY: Record<string, string> = { medicine_types: 'mpc_types_v1', test_library: 'mpc_testlib_v1' };

/** Saved per signed-in account (and family), not shared with other members. */
export const USER_KEYS = new Set(['mpc_active_person_v1', 'medcalc_calc_range_v1', 'mpc_alerts_on_v1']);
/** Kept on this device only, never synced. */
export const DEVICE_KEYS = new Set(['mpc_notif_perm_v1', 'mpc_bell_alerted_v1']);

export type KeySpec =
  | { kind: 'people' }
  | { kind: 'patient'; table: PatientTable; patientId: string }
  | { kind: 'list'; table: DataTable }
  | { kind: 'setting'; key: string }
  | { kind: 'user'; key: string }
  | { kind: 'device'; key: string };

export function parseKey(key: string): KeySpec {
  if (key === PEOPLE_KEY) return { kind: 'people' };
  if (DEVICE_KEYS.has(key)) return { kind: 'device', key };
  if (USER_KEYS.has(key)) return { kind: 'user', key };
  if (key in FAMILY_LIST_KEYS) return { kind: 'list', table: FAMILY_LIST_KEYS[key] };
  const sep = key.indexOf('::');
  if (sep > 0) {
    const prefix = key.slice(0, sep);
    const table = PATIENT_PREFIX[prefix];
    if (table) return { kind: 'patient', table, patientId: key.slice(sep + 2) };
  }
  return { kind: 'setting', key };
}

export function keyFor(table: DataTable, patientId?: string, settingKey?: string): string {
  if (table === 'patients') return PEOPLE_KEY;
  if (table === 'family_settings' || table === 'user_settings') return settingKey!;
  if (table in LIST_TABLE_KEY) return LIST_TABLE_KEY[table];
  return `${TABLE_PREFIX[table as PatientTable]}::${patientId}`;
}

export function tableOf(spec: KeySpec): DataTable | null {
  switch (spec.kind) {
    case 'people': return 'patients';
    case 'patient': return spec.table;
    case 'list': return spec.table;
    case 'setting': return 'family_settings';
    case 'user': return 'user_settings';
    default: return null;
  }
}

/** Keys the original app treats as "its own" (used by backup export). */
export function isAppKey(k: string): boolean {
  return k.startsWith('mpc_') || k.startsWith('medcalc_');
}
